<template>
  <b-form>
    <div class="rounded shadow bg-white mb-4 p-4">
      <h3 class="box-title mb-4">Credit Card Details</h3>
      <b-form-group :label="labels.cardNumber" class="position-relative" :state="validateState('cardNumber')"
      :invalid-feedback="getErrorMessage('cardNumber')">
        <input type="text" v-model="form.cardNumber" id="card-number" :state="validateState('cardNumber')"
          class="form-control bg-transparent" inputmode="numeric"
          pattern="[0-9]*" :placeholder="`Enter ${labels.cardNumber}`" autocomplete="off" autofocus
          @keypress="filterNumeric" maxlength="16" @keyup="preValidateField('cardNumber')" />
        <img v-if="cardBrand" :src="require(`@/assets/images/${cardBrand}.png`)" class="card-brand" :class="cardBrand"
          alt="Card Brand" />
      </b-form-group>
      <b-row>
        <b-col md="6">
          <b-form-group :label="labels.expirationDate" :state="validateState('expirationDate')"
          :invalid-feedback="getErrorMessage('expirationDate')">
            <input type="text" v-model="form.expirationDate" id="expiration-date" :state="validateState('expirationDate')"
              class="form-control bg-transparent"
              placeholder="MM/YY" autocomplete="off" @keyup="formatMonthAndYear" @keydown="formatMonthAndYear"
              maxlength="5" @keypress="filterNumeric" @input="preValidateField('expirationDate')" />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group :label="labels.cvv" :state="validateState('cvv')"
          :invalid-feedback="getErrorMessage('cvv')">
            <input type="password" v-model="form.cvv" id="cvv" :state="validateState('cvv')"
              class="form-control bg-transparent"
              :placeholder="`Enter ${labels.cvv}`" inputmode="numeric" pattern="[0-9]*" autocomplete="off"
              @keypress="filterNumeric" maxlength="3" @input="preValidateField('cvv')" />
          </b-form-group>
        </b-col>
      </b-row>
    </div>
    <div class="rounded shadow bg-white mb-4 p-4">
      <h3 class="box-title mb-4">Billing Details</h3>
      <b-row>
        <b-col md="6">
          <b-form-group :label="labels.firstName" :state="validateState('firstName')"
          :invalid-feedback="getErrorMessage('firstName')">
            <input type="text" v-model="form.firstName" id="first-name" :state="validateState('firstName')"
              class="form-control bg-transparent"
              :placeholder="`Enter ${labels.firstName}`" autocomplete="off" maxlength="255"
              @input="preValidateField('firstName')" />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group :label="labels.lastName" :state="validateState('lastName')"
          :invalid-feedback="getErrorMessage('lastName')">
            <input type="text" v-model="form.lastName" id="last-name" :state="validateState('lastName')"
              class="form-control bg-transparent"
              :placeholder="`Enter ${labels.lastName}`" autocomplete="off" maxlength="255"
              @input="preValidateField('lastName')" />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group :label="labels.mobileNumber" :state="validateState('mobileNumber')"
          :invalid-feedback="getErrorMessage('mobileNumber')">
            <b-input-group id="mobile-number-input-group">
              <template slot="prepend">
                <div class="border rounded-left">
                  <b-dropdown id="mobile-country-code-dropdown" class="base-dropdown bg-white border-0" variant="link"
                    toggle-class="text-decoration-none w-100 rounded-0 border-0" menu-class="w-100" no-caret disabled>
                    <template slot="button-content">
                      <div class="d-flex justify-content-between align-items-center">
                        <img src="@/assets/images/flag-ph-rounded.png" alt="PH" />
                        <div>
                          {{ mobileCountryCode }}
                        </div>
                        <img src="@/assets/images/caret-down.svg" class="base-dropdown-icon" alt="Dropdown" />
                      </div>
                    </template>
                  </b-dropdown>
                </div>
              </template>
              <input type="text" v-model="form.mobileNumber" id="mobile-number" :state="validateState('mobileNumber')"
                class="form-control bg-transparent"
                :placeholder="`Enter ${labels.mobileNumber}`" autocomplete="mobile_1" inputmode="numeric" pattern="[0-9]*"
                @keypress="filterNumeric" maxlength="10" @input="preValidateField('mobileNumber')" />
            </b-input-group>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group :label="labels.emailAddress" :state="validateState('emailAddress')"
          :invalid-feedback="getErrorMessage('emailAddress')">
            <input type="email" v-model="form.emailAddress" id="email-address" :state="validateState('emailAddress')"
              class="form-control bg-transparent"
              :placeholder="`Enter ${labels.emailAddress}`" autocomplete="email-address-1" maxlength="50"
              @input="preValidateField('emailAddress')" />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group :label="labels.addressLine" :state="validateState('addressLine')"
          :invalid-feedback="getErrorMessage('addressLine')">
            <input type="text" v-model="form.addressLine" id="address-line-1" :state="validateState('addressLine')"
              class="form-control bg-transparent"
              :style="[isMobileScreen ? { height: '75px' } : { height: '45px' }]" placeholder="Enter Address Line"
              autocomplete="addres-1" maxlength="255" @input="preValidateField('addressLine')" />
          </b-form-group>
          <b-form-group label="Country">
            <b-dropdown class="dropdown-select" variant="link" toggle-class="text-decoration-none w-100"
              menu-class="w-100" no-caret disabled>
              <template slot="button-content">
                <b-row>
                  <b-col class="text-left text-dark">
                    {{ form.country.name }}
                  </b-col>
                  <b-col class="text-right">
                    <img src="@/assets/images/caret-down.svg" alt="Dropdown" style="height: 15px; width: 15px;" />
                  </b-col>
                </b-row>
              </template>
            </b-dropdown>
          </b-form-group>
          <b-form-group label="Province" :state="validateState('province')"
          :invalid-feedback="getErrorMessage('province')">
            <b-dropdown id="province-dropdown" class="dropdown-select border-0" variant="link"
              toggle-class="text-decoration-none w-100"
              menu-class="w-100" no-caret>
              <template slot="button-content">
                <b-row>
                  <b-col>
                    <div class="text-left"
                      :style="[form.province ? { color: '#495057' } : { 'padding-top': 0, color: '#D8DCDE' }]">
                      {{ form.province ? form.province : "Select" }}
                    </div>
                  </b-col>
                  <b-col class="text-right">
                    <img src="@/assets/images/caret-down.svg" alt="Dropdown" style="height: 15px; width: 15px;" />
                  </b-col>
                </b-row>
              </template>
              <template #default>
                <div class="dropdown-select-menu">
                  <b-dropdown-item v-for="(province, index) in provincesList" :key="index"
                    @click="selectProvince(province)">
                    {{ province.name }}
                  </b-dropdown-item>
                </div>
              </template>
            </b-dropdown>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group :label="labels.city" :state="validateState('city')"
          :invalid-feedback="getErrorMessage('city')">
            <b-dropdown id="city-dropdown"
              :class="['dropdown-select', { 'border-0': form.province }]"
              variant="link" toggle-class="text-decoration-none w-100"
              menu-class="w-100" no-caret :disabled="!form.province">
              <template slot="button-content">
                <b-row>
                  <b-col>
                    <div class="text-left"
                      :style="[form.city ? { color: '#495057' } : { 'padding-top': 0, color: '#D8DCDE' }]">
                      {{ form.city ? form.city : "Select" }}
                    </div>
                  </b-col>
                  <b-col class="text-right">
                    <img src="@/assets/images/caret-down.svg" alt="Dropdown" style="height: 15px; width: 15px;" />
                  </b-col>
                </b-row>
              </template>
              <template #default>
                <div class="dropdown-select-menu">
                  <b-dropdown-item v-for="(city, index) in citiesList" :key="index" @click="selectCity(city)">
                    <div style="white-space: normal">
                      {{ city.name }}
                    </div>
                  </b-dropdown-item>
                </div>
              </template>
            </b-dropdown>
            <b-popover v-if="!form.province" target="city-dropdown" triggers="hover" placement="top">
              Please select a province first.
            </b-popover>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group :label="labels.postalCode" :state="validateState('postalCode')"
          :invalid-feedback="getErrorMessage('postalCode')">
            <input type="text" v-model="form.postalCode" id="postal-code" :state="validateState('postalCode')"
              class="form-control bg-transparent" placeholder="1234"
              autocomplete="off" inputmode="numeric" pattern="[0-9]*" @keydown="filterNumeric" maxlength="4"
              @input="preValidateField('postalCode')" />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group class="mt-4">
            <b-button variant="primary" class="save-credit-card" @click="preValidateForm()" :disabled="isSaveDisabled">
              Save
            </b-button>
          </b-form-group>
        </b-col>
      </b-row>
    </div>
  </b-form>
</template>

<script>
import { defineComponent, ref, computed, watch, onMounted, onBeforeUnmount } from "@vue/composition-api";
import { provinces, cities } from "philippines";
import _ from "lodash";
import { useVuelidate } from "@vuelidate/core";
import { required, helpers, email } from "@vuelidate/validators";

export default defineComponent({
  setup(props, { root }) {
    const form = ref({
      cardNumber: null,
      expirationDate: null,
      cvv: null,
      firstName: null,
      lastName: null,
      country: {
        code: "PH",
        name: "Philippines",
        currency: "PHP",
      },
      addressLine: null,
      postalCode: null,
      province: null,
      city: null,
      emailAddress: null,
      mobileNumber: null
    });

    const labels = {
      cardNumber: "Card Number",
      expirationDate: "Expiration Date (MM/YY)",
      cvv: "CVV",
      firstName: "First Name",
      lastName: "Last Name",
      country: "Country",
      addressLine: "Address Line (Village or Bldg. Name and Apt Unit No., Street Address)",
      province: "Province",
      postalCode: "Postal Code",
      city: "City",
      emailAddress: "Email Address",
      mobileNumber: "Mobile Number"
    };

    const provincesList = _.sortBy(provinces, ["name"]);
    let citiesList = ref(_.sortBy(cities, ["name"]));
    const cardBrand = ref(null);
    const mobileCountryCode = "+63";
    const maxMobileWidth = 767;
    const isMobileScreen = ref(false);
    const xenditCard = ref(null);
    const plan = computed(() => root.$store.state.myPlan.plan);
    const paymentErrors = computed(() => root.$store.state.myPlan.errors);

    onMounted(() => {
      isMobileScreen.value = window.innerWidth < maxMobileWidth;
      window.addEventListener("resize", checkIfMobileScreen);
      root.$on('createXenditToken', () => createXenditToken());
      const isXendit = setInterval(() => {
        if (window.Xendit) {
          window.Xendit.setPublishableKey(process.env.VUE_APP_XENDIT_PUBLISHABLE_KEY);
          xenditCard.value = window.Xendit.card;
          clearInterval(isXendit);
        }
      }, 300);
      if (!plan.value) root.$store.dispatch("myPlan/getPlanDetails");
    });

    onBeforeUnmount(() => {
      root.$off('createXenditToken');
      window.removeEventListener("resize", checkIfMobileScreen);
    });

    const isSaveDisabled = computed(() => {
      const { phoneNumber, ...requiredFields } = form.value;
      return !!Object.keys(requiredFields).map(key => requiredFields[key]).includes(null)
        || !!Object.keys(requiredFields).map(key => requiredFields[key]).includes('')
    });

    const isNotNumeric = (input) => /\D/g.test(input);

    const validatorMessages = {
      required: "is required.",
      alphanum: "may only contain alphanumeric characters.",
      invalidCardNumber: "Please enter a valid credit card number.",
      invalidExpirationDate: "Please enter a valid expiration date."
    };

    const mustBeCardNumber = /^\d{16}$/;
    const mustBeMobileNumber = /^9\d{9}$/;
    const mustBePostalCode = /^\d{4}$/;
    const mustBeAlphaNumericSpace = /^[\w ]*$/;
    const mustBeValidExpiration = /^\d{2}\/\d{2}$/;

    const rules = {
      cardNumber: {
        required: helpers.withMessage(`Card number ${validatorMessages.required}`, required),
        validCardNumber: helpers.withMessage(validatorMessages.invalidCardNumber,
          (cardNumber) => !helpers.req(cardNumber) || mustBeCardNumber.test(cardNumber) || xenditCard.value.validateCardNumber(cardNumber))
      },
      expirationDate: {
        required: helpers.withMessage(`Expiration date ${validatorMessages.required}`, required),
        mustBeValidExpiration: helpers.withMessage(validatorMessages.invalidExpirationDate,
          (expirationDate) => !helpers.req(expirationDate) || checkExpirationDate()
        )
      },
      cvv: {
        required: helpers.withMessage(`CVV ${validatorMessages.required}`, required)
      },
      firstName: {
        required: helpers.withMessage(`First name ${validatorMessages.required}`, required),
        mustBeAlphaNumericSpace: helpers.withMessage(`First name ${validatorMessages.alphanum}`,
          (firstName) => !helpers.req(firstName) || mustBeAlphaNumericSpace.test(firstName))
      },
      lastName: {
        required: helpers.withMessage(`Last name ${validatorMessages.required}`, required),
        mustBeAlphaNumericSpace: helpers.withMessage(`Last name ${validatorMessages.alphanum}`,
          (lastName) => !helpers.req(lastName) || mustBeAlphaNumericSpace.test(lastName))
      },
      mobileNumber: {
        required: helpers.withMessage(`Mobile number ${validatorMessages.required}`, required),
        mustBeMobileNumber: helpers.withMessage("Please enter a valid mobile number.",
          (mobileNumber) => !helpers.req(mobileNumber) || mustBeMobileNumber.test(mobileNumber))
      },
      emailAddress: {
        required: helpers.withMessage(`Email address ${validatorMessages.required}`, required),
        email: helpers.withMessage("Please enter a valid email address.", email)
      },
      addressLine: {
        required: helpers.withMessage(`Address line ${validatorMessages.required}`, required)
      },
      province: {
        required: helpers.withMessage(`Province ${validatorMessages.required}`, required)
      },
      city: {
        required: helpers.withMessage(`City ${validatorMessages.required}`, required)
      },
      postalCode: {
        required: helpers.withMessage(`Postal code ${validatorMessages.required}`, required),
        mustBePostalCode: helpers.withMessage("Please enter a valid postal code.",
          (postalCode) => !helpers.req(postalCode) || mustBePostalCode.test(postalCode))
      }
    };

    const v$ = useVuelidate(rules, form);

    const filterNumeric = (e) => {
      const backspaceKeyCode = 8;

      if (e.keyCode === backspaceKeyCode) return;

      if (isNotNumeric(String.fromCharCode(e.keyCode))) e.preventDefault();
    };

    const formatMonthAndYear = (event) => {
      const backspaceKeyCode = 8;

      if (event.keyCode === backspaceKeyCode) return;

      event.target.value = event.target.value
        .replace(/^([1-9]\/|[2-9])$/g, "0$1/")
        .replace(/^(0[1-9]|1[0-2])$/g, "$1/")
        .replace(/^([0-1])([3-9])$/g, "0$1/$2")
        .replace(/^(0?[1-9]|1[0-2])(\d{2})$/g, "$1/$2")
        .replace(/^([0]+)\/|[0]+$/g, "0")
        .replace(/[^\d/]|^[/]*$/g, "")
        .replace(/\/\//g, "/");
    };

    const selectProvince = (province) => {
      form.value.province = province.name;
      citiesList.value = cities.filter((city) => city.province === province.key);
    };

    const selectCity = (city) => {
      form.value.city = city.name;
    };

    const preValidateForm = () => {
      let form = [
        'cardNumber',
        'expirationDate',
        'cvv',
        'firstName',
        'lastName',
        'mobileNumber',
        'emailAddress',
        'addressLine',
        'postalCode'
      ];

      form.forEach(field => {
        preValidateField(field);
      });
      v$.value.$touch();

      if (!v$.value.$errors.length)
        root.$emit('bv::show::modal', 'confirm-save-credit-card-modal')
    };

    const preValidateField = (field) => {
      switch (field) {
        case "cardNumber":
          checkCardNumber();
          break;
        case "expirationDate":
          checkExpirationDate();
          break;
        case "cvv":
          checkCvv();
          break;
        case "mobileNumber":
          checkMobileNumber();
          break;
        case "emailAddress":
          checkEmailAddress();
          break;
        case "postalCode":
          checkPostalCode();
          break;
        default:
          v$.value[field].$touch();
      }
    };

    const checkCardNumber = () => {
      form.value.cardNumber = form.value.cardNumber.replace(/\D/g, "").slice(0, 16);
      cardBrand.value = getCardBrand(form.value.cardNumber);
      v$.value.cardNumber.$touch();
    };

    const getCardBrand = (cardNumber) => {
      const visa = /^4\d{12}(?:\d{3})?$/;
      const mastercard =
        /^(?:5[1-5]\d{2}|222[1-9]|22[3-9]\d|2[3-6]\d{2}|27[01]\d|2720)\d{12}$/;
      const jcb = /^(?:2131|1800|35\d{3})\d{11}$/;

      if (visa.test(cardNumber)) return "visa";

      if (mastercard.test(cardNumber)) return "mastercard";

      if (jcb.test(cardNumber)) return "jcb";

      return "";
    };

    const checkExpirationDate = () => {
      form.value.expirationDate = form.value.expirationDate.replace(/[^0-9/]/g, "");
      const { expirationDate } = form.value;

      if (expirationDate) {
        if (mustBeValidExpiration.test(expirationDate)) {
          const [month, year] = expirationDate.split("/");

          if (xenditCard.value.validateExpiry(month, 2000 + parseInt(year))) v$.value.expirationDate.$reset();

          return xenditCard.value.validateExpiry(month, 2000 + parseInt(year));
        }
      }
      v$.value.expirationDate.$touch();
      return false;
    };

    const checkCvv = () => {
      form.value.cvv = form.value.cvv.replace(/\D/g, "").slice(0, 3);
      v$.value.cvv.$touch();
    };

    const checkMobileNumber = () => {
      form.value.mobileNumber = form.value.mobileNumber.replace(/\D/g, "").slice(0, 10);
      v$.value.mobileNumber.$touch();
    };

    const checkEmailAddress = () => {
      v$.value.emailAddress.$touch();
    };

    const checkPostalCode = () => {
      form.value.postalCode = form.value.postalCode.replace(/\D/g, "").slice(0, 4);
      v$.value.postalCode.$touch();
    };

    const checkIfMobileScreen = () => {
      isMobileScreen.value = window.innerWidth < maxMobileWidth;
    };

    const createXenditToken = async () => {
      const [month, year] = form.value.expirationDate.split("/");

      const tokenPayload = {
        amount: parseFloat(plan.value.charges_total_amount).toFixed(2),
        card_number: form.value.cardNumber,
        card_exp_month: month,
        card_exp_year: (2000 + parseInt(year)).toString(),
        card_cvn: form.value.cvv,
        is_multiple_use: true,
        should_authenticate: false,
        card_holder_email: form.value.emailAddress,
        card_holder_first_name: form.value.firstName,
        card_holder_last_name: form.value.lastName,
        card_holder_phone_number: `${mobileCountryCode}${form.value.mobileNumber}`,
        billing_details: {
          address: {
            country: form.value.country.code,
            street_line1: form.value.addressLine,
            city: form.value.city,
            province_state: form.value.province,
            postal_code: form.value.postalCode,
          }
        }
      };

      await xenditCard.value.createToken(tokenPayload, async (cardTokenError, cardTokenReponse) => {
        if (cardTokenError) {
          root.$emit('bv::hide::modal', 'confirm-save-credit-card-modal');
          root.$emit('showXenditPromptModal', { message: cardTokenError.message });

          return;
        }
        if (cardTokenReponse.id) {
          const authPayload = {
            amount: parseFloat(plan.value.charges_total_amount).toFixed(2),
            token_id: cardTokenReponse.id,
            currency: form.value.country.currency,
            card_data: {
              card_holder_email: form.value.emailAddress,
              card_holder_first_name: form.value.firstName,
              card_holder_last_name: form.value.lastName,
              card_holder_phone_number: `${mobileCountryCode}${form.value.mobileNumber}`,
            }
          };
          await createXenditAuthentication(authPayload, cardTokenReponse);
        } else {
          root.$emit("showXenditPromptModal", { message: "Something went wrong." });
        }
      });
    };

    const createXenditAuthentication = (authPayload, cardTokenReponse) => {
      xenditCard.value.createAuthentication(authPayload, async (cardAuthError, cardAuthResponse) => {
        if (cardAuthError) {
          root.$emit('bv::hide::modal', 'confirm-save-credit-card-modal');
          root.$emit('showXenditPromptModal', { message: cardAuthError.message });

          return;
        }

        switch (cardAuthResponse.status) {
          case "VERIFIED":
            addPaymentMethod(cardTokenReponse, cardAuthResponse);
            break;
          case "IN_REVIEW":
            root.$emit('bv::hide::modal', 'confirm-save-credit-card-modal');
            root.$emit('setAuthenticationConfigs', {
              authenticationUrl: cardAuthResponse.payer_authentication_url,
              isOpenAuthenticationModal: true
            });
            break;
          case "FAILED":
            root.$emit('bv::hide::modal', 'confirm-save-credit-card-modal');
            root.$emit("showXenditPromptModal", {
              message: `${_.capitalize(
                _.lowerCase(cardAuthResponse.failure_reason)
              )}.`,
            });
            break;
          default:
            root.$emit('bv::hide::modal', 'confirm-save-credit-card-modal');
            root.$emit('showXenditPromptModal', { message: "Something went wrong." });
        }
      });
    };

    const addPaymentMethod = async (cardTokenReponse, cardAuthResponse) => {
      const addPaymentMethodPayload = {
        token_id: cardTokenReponse.id,
        xendit_response: JSON.stringify({
          token_response: cardTokenReponse,
          authentication_response: cardAuthResponse,
          masked_card_number:
            cardTokenReponse.masked_card_number,
          ...(cardAuthResponse.id && {
            authentication_id: cardAuthResponse.id,
          })
        })
      };

      root.$emit('setAuthenticationConfigs', {
        authenticationUrl: null,
        isOpenAuthenticationModal: false
      });

      await root.$store.dispatch("myPlan/savePaymentMethod", addPaymentMethodPayload);

      if (_.isEmpty(paymentErrors.value)) {
        setTimeout(() => {
          root.$router.push({ name: 'MyPlanDetails' });
        }, 300);
      }
    };

    const validateState = field =>
      v$.value[field].$dirty ? !v$.value[field].$error : null;
    const getErrorMessage = field =>
      v$.value[field].$errors.length ? v$.value[field].$errors[0].$message : "";

    return {
      form,
      labels,
      provincesList,
      citiesList,
      cardBrand,
      mobileCountryCode,
      isMobileScreen,
      isSaveDisabled,
      filterNumeric,
      formatMonthAndYear,
      selectProvince,
      selectCity,
      preValidateForm,
      preValidateField,
      validateState,
      getErrorMessage
    }
  }
})
</script>

<style lang="scss" scoped>
.form-control {
  height: 44px;

  &::placeholder {
    color: #D8DCDE;
  }

  &.is-invalid {
    border-color: #CE0E2D;
  }
}

.card-brand {
  width: 30px;
  position: absolute;
  right: 13px;

  &.visa {
    top: 18px;
  }

  &.mastercard {
    top: 12px;
  }

  &.jcb {
    top: 10px;
  }
}

.dropdown-select {
  width: 100%;
  height: 45px;
  border: 1px solid #ced4da;
  border-radius: 6px;
}

.dropdown-select-menu {
  height: 200px;
  overflow-y: auto;
}

#mobile-number-input-group {
  .input-group-prepend .border {
    border-color:  #ced4da !important;
  }

  .input-group-text {
    height: 44px;
  }

  .form-control {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    border-top-right-radius: 0.25rem;
    border-bottom-right-radius: 0.25rem;
  }
}

#mobile-country-code-dropdown {
  height: 42px;
  width: 87px;
}

.save-credit-card {
  max-width: 100px;
  width: 100%;
  border-radius: 20px;
}
</style>

<style lang="scss">
#xendit-card-authentication {
  .modal-full-dialog {
    width: 95%;
    height: 92%;
    padding: 0;
  }

  .modal-full-content {
    height: 99%;
  }
}

#province-dropdown,
#city-dropdown {
  .is-invalid {
    border-color: #CE0E2D !important;
  }
}

#city-dropdown.is-invalid {
  border-color: #CE0E2D !important;
}
</style>
